import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import Header from '../components/Header';
import Footer from '../components/Footer';

const SubmissionSuccess = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger animations after component mounts
    setIsVisible(true);

    // Confetti celebration effect
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    function randomInRange(min, max) {
      return Math.random() * (max - min) + min;
    }

    // Initial burst
    confetti({
      ...defaults,
      particleCount: 100,
      origin: { x: 0.5, y: 0.5 }
    });

    // Continuous confetti
    const interval = setInterval(function () {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);

      // Left side
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });

      // Right side
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-dark-bg text-white min-h-screen flex flex-col font-display">
      <Header variant="dark-form" />

      <main className="flex-grow flex items-center justify-center px-6 py-20">
        <div className="max-w-[700px] w-full flex flex-col items-center">
          {/* Success Icon with Animation */}
          <div
            className={`mb-8 relative transition-all duration-700 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
              }`}
          >
            <div className="absolute inset-0 bg-green-500/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="relative bg-dark-card border-2 border-green-500 rounded-full p-8 animate-[wiggle_1s_ease-in-out]">
              <span className="material-symbols-outlined text-green-400 animate-[checkmark_0.6s_ease-in-out]" style={{ fontSize: '80px' }}>
                check_circle
              </span>
            </div>
          </div>

          {/* Success Message */}
          <div
            className={`text-center mb-12 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
          >
            <h1 className="text-white text-4xl md:text-5xl font-bold tracking-tight mb-4">
              Payment Successful!
            </h1>
            <p className="text-gray-400 text-lg font-normal max-w-[500px] mx-auto">
              Your project has been submitted successfully. We've sent a confirmation email with all the details.
            </p>
          </div>

          {/* Info Cards */}
          <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
            <div
              className={`bg-dark-card border border-border-gray rounded-xl p-6 flex items-start gap-4 transition-all duration-700 delay-300 hover:border-blue-400 hover:shadow-lg hover:shadow-blue-400/20 hover:-translate-y-1 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
            >
              <span className="material-symbols-outlined text-blue-400 text-2xl flex-shrink-0 transition-transform hover:scale-110">email</span>
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-1">Email Confirmation</h3>
                <p className="text-gray-400 text-sm">Check your inbox for project details and next steps.</p>
              </div>
            </div>

            <div
              className={`bg-dark-card border border-border-gray rounded-xl p-6 flex items-start gap-4 transition-all duration-700 delay-400 hover:border-purple-400 hover:shadow-lg hover:shadow-purple-400/20 hover:-translate-y-1 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
            >
              <span className="material-symbols-outlined text-purple-400 text-2xl flex-shrink-0 transition-transform hover:scale-110">schedule</span>
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-1">Response Time</h3>
                <p className="text-gray-400 text-sm">Our team will review and contact you within 24-48 hours.</p>
              </div>
            </div>

            <div
              className={`bg-dark-card border border-border-gray rounded-xl p-6 flex items-start gap-4 transition-all duration-700 delay-500 hover:border-green-400 hover:shadow-lg hover:shadow-green-400/20 hover:-translate-y-1 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
            >
              <span className="material-symbols-outlined text-green-400 text-2xl flex-shrink-0 transition-transform hover:scale-110">support_agent</span>
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-1">Need Help?</h3>
                <a href="mailto:projecktocare@gmail.com" className="text-blue-400 hover:text-blue-300 text-sm transition-colors break-all">
                  projecktocare@gmail.com
                </a>
              </div>
            </div>
          </div>

          {/* What's Next Section */}
          <div
            className={`w-full bg-dark-card border border-border-gray rounded-xl p-8 mb-12 transition-all duration-700 delay-600 hover:border-white/30 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
          >
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-border-gray">
              <span className="material-symbols-outlined text-white text-2xl">trending_flat</span>
              <h2 className="text-white text-xl font-bold tracking-tight">What Happens Next?</h2>
            </div>

            <div className="space-y-4">
              <div
                className={`flex items-start gap-4 transition-all duration-500 delay-700 hover:translate-x-2 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                  }`}
              >
                <div className="bg-dark-bg rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mt-1 transition-all hover:bg-white hover:text-black">
                  <span className="text-white font-bold text-sm">1</span>
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Project Review</h4>
                  <p className="text-gray-400 text-sm">Our team will carefully review your requirements and assess project feasibility.</p>
                </div>
              </div>

              <div
                className={`flex items-start gap-4 transition-all duration-500 delay-800 hover:translate-x-2 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                  }`}
              >
                <div className="bg-dark-bg rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mt-1 transition-all hover:bg-white hover:text-black">
                  <span className="text-white font-bold text-sm">2</span>
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Initial Consultation</h4>
                  <p className="text-gray-400 text-sm">We'll reach out to discuss timelines, milestones, and any clarifications needed.</p>
                </div>
              </div>

              <div
                className={`flex items-start gap-4 transition-all duration-500 delay-900 hover:translate-x-2 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                  }`}
              >
                <div className="bg-dark-bg rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mt-1 transition-all hover:bg-white hover:text-black">
                  <span className="text-white font-bold text-sm">3</span>
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Project Kickoff</h4>
                  <p className="text-gray-400 text-sm">Once everything is confirmed, we'll begin development and keep you updated throughout.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div
            className={`flex flex-col sm:flex-row gap-4 w-full justify-center transition-all duration-700 delay-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
          >
            <button
              onClick={() => navigate('/')}
              className="bg-white hover:bg-gray-200 text-black h-14 px-10 rounded-xl text-base font-bold tracking-tight transition-all active:scale-[0.99] hover:scale-105 hover:shadow-xl flex items-center justify-center gap-3 group"
            >
              <span className="material-symbols-outlined transition-transform group-hover:-translate-x-1">home</span>
              Return Home
            </button>

            <button
              onClick={() => navigate('/start')}
              className="bg-transparent border-2 border-white hover:bg-white hover:text-black text-white h-14 px-10 rounded-xl text-base font-bold tracking-tight transition-all active:scale-[0.99] hover:scale-105 hover:shadow-xl flex items-center justify-center gap-3 group"
            >
              <span className="material-symbols-outlined transition-transform group-hover:rotate-90">add</span>
              Submit Another Project
            </button>
          </div>
        </div>
      </main>

      <Footer variant="dark-form" />

      {/* Custom Keyframe Animations */}
      <style>{`
        @keyframes checkmark {
          0% {
            transform: scale(0) rotate(-45deg);
            opacity: 0;
          }
          50% {
            transform: scale(1.2) rotate(10deg);
          }
          100% {
            transform: scale(1) rotate(0deg);
            opacity: 1;
          }
        }
        
        @keyframes wiggle {
          0%, 100% {
            transform: rotate(0deg);
          }
          25% {
            transform: rotate(-5deg);
          }
          75% {
            transform: rotate(5deg);
          }
        }
      `}</style>
    </div>
  );
};

export default SubmissionSuccess;
